import React, { useState } from 'react';
import api from '@/api';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { archivedDataOf, isArchived } from '@/utils/archive';

/**
 * A quiet note at the top of a trial that uses archived master data (the trial itself is not archived
 * and works as usual): "This trial uses archived data: CO 2, Host Hansen".
 */
export function ArchivedDataBanner({ trial }) {
  const archived = archivedDataOf(trial);
  if (archived.length === 0) return null;
  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200" role="note" data-testid="archived-data-banner">
      This trial uses archived data: {archived.map(({ label, value }) => `${label} ${value}`).join(', ')}
    </div>
  );
}

/** The "Show archived" switch above a master data table. */
export function ShowArchivedToggle({ checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium">
      <input type="checkbox" className="rounded" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      Show archived
    </label>
  );
}

/** "Archived" next to an archived row's name. */
export function ArchivedBadge({ row }) {
  if (!isArchived(row)) return null;
  return (
    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" data-testid="archived-badge">
      Archived
    </span>
  );
}

/**
 * A master data row's Delete (or, for types never deleted, Archive) and Reactivate buttons, for the
 * admin-only master data pages: "Archive" and "Delete" side by side (one row in use can only be
 * archived; the table does not know which rows are in use until asked). Both open ConfirmDeleteDialog,
 * which shows what uses the row. Delete on a row in use says "<Noun> <name> is in use" and offers only
 * "Archive instead"; otherwise the name must be typed.
 *
 * - type: the API's master data segment ("crops", "contracts" ...)
 * - name: what must be typed; nameLabel: what it is called; noun: e.g. "crop" (in "Delete crop")
 * - archiveOnly: no delete at all (customers)
 * - onChanged(): after a delete, archive or reactivation
 */
export default function MasterDataRowActions({ type, row, name, nameLabel = 'name', noun, archiveOnly = false, onChanged }) {
  const [mode, setMode] = useState(null); // 'archive' | 'delete' while the dialog is open
  const [busy, setBusy] = useState(false);
  const base = `/masterdata/${type}/${row.id}`;
  const subject = noun.charAt(0).toUpperCase() + noun.slice(1);

  if (isArchived(row)) {
    return (
      <button
        type="button"
        className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 text-xs disabled:opacity-50"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          api.post(`${base}/unarchive`).then(() => onChanged?.()).catch(() => alert('Failed to reactivate')).finally(() => setBusy(false));
        }}
      >
        Reactivate
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 text-xs"
        onClick={() => setMode('archive')}
      >
        Archive
      </button>
      {!archiveOnly && (
        <button
          type="button"
          className="px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200 text-xs"
          onClick={() => setMode('delete')}
        >
          Delete
        </button>
      )}
      <ConfirmDeleteDialog
        open={mode !== null}
        title={`${mode === 'archive' ? 'Archive' : 'Delete'} ${noun}`}
        subject={subject}
        name={name}
        nameLabel={nameLabel}
        usageUrl={`${base}/usage`}
        archiveOnly={mode === 'archive'}
        onDelete={() => api.delete(base)}
        onArchive={() => api.post(`${base}/archive`)}
        onClose={() => setMode(null)}
        onDone={() => {
          setMode(null);
          onChanged?.();
        }}
      />
    </>
  );
}
